import { useState } from 'react'

import Button from 'common/components/button/Button'
import { Export } from 'common/icons'

import MessageCard from './MessageCard'
import PersonaCard from './PersonaCard'

const NY_PERSONA = [
  'Traveller type: Solo',
  'Age band: 25–34',
  'Destination: New York',
  'Lead time: 0–7 days',
  'Traveller profile: Budget boutique booker',
]

const PARIS_PERSONA = [
  'Traveller type: Couple',
  'Age band: 35–44',
  'Destination: Paris',
  'Lead time: 14–28 days',
  'Traveller profile: Leisure explorer',
]

const ContextualEmptyState = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false)

  return (
    <>
      <div className="flex flex-col flex-grow bg-gold-40 p-6">
        <div className="flex flex-col flex-grow bg-white overflow-hidden rounded-lg">
          <div
            className="relative flex-grow bg-gold-50 overflow-hidden"
            style={{ minHeight: 600 }}
          >
            <img
              src="/images/array-lines.png"
              alt=""
              className="absolute top-0 left-0 w-auto h-[50%] sm:h-[60%] md:h-[75%] lg:h-[90%] xl:h-auto"
            />

            {/* Center copy */}
            <div
              className="absolute inset-0 flex flex-col items-center justify-start gap-6 px-8 text-center"
              style={{ paddingTop: 100 }}
            >
              <h2 className="text-4xl font-bold text-base-900">
                Get started with Contextual
              </h2>
              <p className="text-base text-base-700 max-w-5xl leading-relaxed">
                Jacquard's Contextual Messaging turns what you offer and your
                customer data into a content engine — delivering messaging
                that's relevant to every individual, at any volume, finding the
                right message for each person.
              </p>
              <div className="flex gap-4">
                <Button
                  variant="secondary"
                  prefixIcon={<Export size={4} />}
                  onClick={() => setIsVideoOpen(true)}
                >
                  Learn more
                </Button>
                <a href="mailto:sales@jacquard.com?subject=Please%20get%20in%20touch%20about%20Contextual%20Messaging!&body=I%20saw%20the%20Contextual%20Messaging%20information%20in%20the%20Jacquard%20platform%20and%20I%27d%20like%20to%20hear%20more%20about%20this.%20Please%20get%20in%20touch.">
                  <Button variant="primary">Activate Contextual →</Button>
                </a>
              </div>
            </div>

            {/* Left group — New York: hidden below 1200px */}
            <div
              className="hidden md:block"
              style={{
                position: 'absolute',
                bottom: 30,
                left: 0,
                width: 620,
                height: 282,
              }}
            >
              <img
                src="/images/pexels-katya-wolf-8734422.svg"
                alt="New York"
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 275,
                  width: 179,
                  height: 255,
                  objectFit: 'cover',
                  borderRadius: 12,
                  zIndex: 1,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 24,
                  left: 24,
                  zIndex: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <MessageCard
                  header="Email Subject Line"
                  text="<highlight>New York</highlight> doesn't wait – neither should <border>you</border>."
                  width={280}
                />
                <MessageCard
                  header="Email Preheader"
                  text="<highlight>Lower East Side</highlight>, late <border>nights</border>, no itinerary needed – this <border>weekend</border>?"
                  width={257}
                />
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: 110,
                  left: 438,
                  zIndex: 2,
                }}
              >
                <PersonaCard rows={NY_PERSONA} />
              </div>
            </div>

            {/* Right group — Paris: hidden below 1650px */}
            <div
              className="hidden lg-plus:block"
              style={{
                position: 'absolute',
                bottom: 30,
                right: 0,
                width: 620,
                height: 282,
              }}
            >
              <img
                src="/images/pexels-ivan-samkov-8951563.svg"
                alt="Paris"
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 209,
                  width: 179,
                  height: 255,
                  objectFit: 'cover',
                  borderRadius: 12,
                  zIndex: 1,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 24,
                  right: 373,
                  zIndex: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <MessageCard
                  header="Email Subject Line"
                  text="<border>Paris</border> has been <highlight>saving a table</highlight> for you."
                  width={280}
                />
                <MessageCard
                  header="Email Preheader"
                  text="The <highlight>Musée d'Orsay</highlight>, the finest <highlight>restaurants</highlight> – your trip, curated."
                  width={257}
                />
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: 110,
                  right: 20,
                  zIndex: 2,
                }}
              >
                <PersonaCard rows={PARIS_PERSONA} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isVideoOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.7)',
          }}
          onClick={() => setIsVideoOpen(false)}
        >
          <div
            style={{ position: 'relative', width: '90vw', height: '85vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={{
                position: 'absolute',
                top: '-2.25rem',
                right: 0,
                color: 'white',
                fontSize: '1.5rem',
                lineHeight: 1,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={() => setIsVideoOpen(false)}
            >
              ✕
            </button>
            <iframe
              src="https://www.youtube.com/embed/7zI5dtpLNY4"
              title="Get started with Contextual"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default ContextualEmptyState
